/**
 * Detail view of the prompts widget (#1825): grows out of the bar, not a
 * window. Search, order, edit, share, copy, delete, and the step into the chat.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Search, X } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PromptShareDialog } from './PromptShareDialog'
import { useToast } from '@/components/ui/use-toast'
import { useWidgetArea } from '@/contexts/WidgetAreaContext'
import { logError } from '@/lib/logger'
import { deletePrompt, reorderPrompts, type UserPrompt } from '@/services/prompt-library-service'
import { PromptEditorDialog } from './PromptEditorDialog'
import { notifyPromptsChanged, usePromptList } from './usePromptList'
import { useSendPromptToChat } from './useSendPromptToChat'

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'
const OUTLINE = `rounded-full border border-[var(--window-border-color)] text-[var(--text-color)] ${FOCUS_RING}`

function PromptRow({
  prompt,
  selected,
  sortable,
  onPick,
}: {
  prompt: UserPrompt
  selected: boolean
  sortable: boolean
  onPick: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: prompt.id,
    disabled: !sortable,
  })
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      aria-pressed={selected}
      onClick={onPick}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className={`min-h-11 w-full touch-none rounded-[10px] px-3 py-2 text-left text-sm text-[var(--text-color)] ${FOCUS_RING} ${
        selected ? 'border border-[var(--theme-interactive-color)] font-medium' : 'border border-transparent'
      }`}
    >
      {prompt.title}
    </button>
  )
}

export function PromptsPanel() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { promptsPanelSelection, closePromptsPanel } = useWidgetArea()
  const { prompts, setPrompts, loading, failed, reload } = usePromptList()
  const sendToChat = useSendPromptToChat()
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | undefined>(promptsPanelSelection)
  const [editor, setEditor] = useState<{ prompt: UserPrompt | null } | null>(null)
  const [sharing, setSharing] = useState<UserPrompt | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !editor && !sharing) closePromptsPanel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [closePromptsPanel, editor, sharing])

  const needle = query.trim().toLowerCase()
  const visible = useMemo(
    () =>
      prompts.filter((prompt) =>
        `${prompt.title} ${prompt.description ?? ''} ${prompt.prompt_text}`.toLowerCase().includes(needle),
      ),
    [prompts, needle],
  )
  const selected = prompts.find((prompt) => prompt.id === selectedId) ?? prompts[0]

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: t('common.success'), description: t('common.copied') })
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'PromptsPanel.copy' })
      toast({ title: t('common.error'), description: t('common.copyFailed'), variant: 'destructive' })
    }
  }

  const remove = async (prompt: UserPrompt) => {
    setDeleting(true)
    try {
      if (!(await deletePrompt(prompt.id))) throw new Error('Deleting the prompt failed')
      setConfirmDelete(false)
      setSelectedId(undefined)
      notifyPromptsChanged()
      toast({ title: t('common.success'), description: t('promptLibrary.deleteSuccess') })
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'PromptsPanel.remove' })
      toast({ title: t('common.error'), description: t('promptLibrary.errors.deleteFailed'), variant: 'destructive' })
    } finally {
      setDeleting(false)
    }
  }

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const ids = prompts.map((prompt) => prompt.id)
    const reordered = arrayMove(prompts, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
    setPrompts(reordered)
    try {
      await reorderPrompts(reordered.map((prompt) => prompt.id))
      notifyPromptsChanged()
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'PromptsPanel.reorder' })
      await reload()
      toast({ title: t('common.error'), description: t('promptLibrary.errors.reorderFailed'), variant: 'destructive' })
    }
  }

  const toChat = (text: string) => {
    sendToChat(text)
    closePromptsPanel()
  }

  return (
    <section
      role="dialog"
      aria-label={t('widgetBar.prompts.panelLabel')}
      data-prompts-panel
      className="fixed bottom-[100px] right-[404px] top-6 z-[13000] flex w-[min(680px,calc(100vw-428px))] flex-col overflow-hidden rounded-3xl border border-[var(--window-border-color)] bg-[var(--window-background)] text-[var(--text-color)] shadow-2xl"
    >
      <div className="flex flex-none items-center gap-3 border-b border-[var(--window-border-color)] py-4 pl-6 pr-4">
        <h2 className="flex-1 text-xl font-bold">{t('widgetBar.names.prompt-library')}</h2>
        <button type="button" onClick={() => setEditor({ prompt: null })} className={`flex h-11 items-center gap-2 px-4 text-sm ${OUTLINE}`}>
          <Plus className="h-4 w-4" aria-hidden />
          {t('widgetBar.prompts.new')}
        </button>
        <button
          type="button"
          aria-label={t('widgetBar.prompts.collapse')}
          onClick={closePromptsPanel}
          className={`flex h-11 w-11 items-center justify-center ${OUTLINE}`}
        >
          <X className="h-[18px] w-[18px]" aria-hidden />
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex w-[272px] flex-none flex-col gap-3 border-r border-[var(--window-border-color)] px-3 py-4">
          <label className="flex h-11 flex-none items-center gap-2 rounded-[10px] border border-[var(--window-border-color)] px-3 text-[var(--text-secondary)]">
            <Search className="h-4 w-4 flex-none" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('widgetBar.prompts.search')}
              aria-label={t('widgetBar.prompts.search')}
              className="min-w-0 flex-1 bg-transparent text-sm text-[var(--text-color)] outline-none"
            />
          </label>
          <div className="flex min-h-0 flex-col gap-1 overflow-y-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={visible.map((prompt) => prompt.id)} strategy={verticalListSortingStrategy}>
                {visible.map((prompt) => (
                  <PromptRow
                    key={prompt.id}
                    prompt={prompt}
                    selected={prompt.id === selected?.id}
                    sortable={!needle}
                    onPick={() => {
                      setSelectedId(prompt.id)
                      setConfirmDelete(false)
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {!loading && !failed && visible.length === 0 && (
              <div className="p-3 text-[13px] text-[var(--text-secondary)]">
                {t(needle ? 'widgetBar.prompts.noHits' : 'widgetBar.prompts.empty')}
              </div>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4 p-6">
          {failed ? (
            <div className="flex flex-col items-start gap-2">
              <span className="text-sm text-[var(--text-secondary)]">{t('widgetBar.prompts.loadFailed')}</span>
              <button type="button" onClick={() => void reload()} className={`h-9 px-3.5 text-[13px] ${OUTLINE}`}>
                {t('widgetBar.prompts.retry')}
              </button>
            </div>
          ) : selected ? (
            <>
              <h3 className="text-lg font-bold">{selected.title}</h3>
              <div className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-[14px] border border-[var(--window-border-color)] p-4 text-sm leading-relaxed">
                {selected.prompt_text}
              </div>
              <button
                type="button"
                onClick={() => toChat(selected.prompt_text)}
                className={`h-12 flex-none rounded-full bg-[var(--theme-interactive-color)] text-[15px] font-bold text-[var(--theme-interactive-foreground)] ${FOCUS_RING}`}
              >
                {t('widgetBar.prompts.toChat')}
              </button>
              {confirmDelete ? (
                <div className="flex flex-none items-center gap-2" role="group">
                  <span className="min-w-0 flex-1 text-[13px]">{t('promptLibrary.deleteConfirm', { title: selected.title })}</span>
                  <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className={`h-11 px-4 text-[13px] ${OUTLINE}`}>
                    {t('common.cancel')}
                  </button>
                  <button type="button" onClick={() => void remove(selected)} disabled={deleting} className={`h-11 px-4 text-[13px] font-bold ${OUTLINE}`}>
                    {t('common.delete')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-none gap-2">
                  <button type="button" onClick={() => setEditor({ prompt: selected })} className={`h-11 flex-1 text-[13px] ${OUTLINE}`}>{t('common.edit')}</button>
                  <button type="button" onClick={() => setSharing(selected)} className={`h-11 flex-1 text-[13px] ${OUTLINE}`}>{t('widgetBar.prompts.share')}</button>
                  <button type="button" onClick={() => void copy(selected.prompt_text)} className={`h-11 flex-1 text-[13px] ${OUTLINE}`}>{t('common.copy')}</button>
                  <button type="button" onClick={() => setConfirmDelete(true)} className={`h-11 flex-1 text-[13px] ${OUTLINE}`}>{t('common.delete')}</button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      <PromptEditorDialog
        open={Boolean(editor)}
        prompt={editor?.prompt ?? null}
        onOpenChange={(open) => !open && setEditor(null)}
        onSaved={(saved) => {
          setSelectedId(saved.id)
          notifyPromptsChanged()
        }}
      />
      <PromptShareDialog open={Boolean(sharing)} onOpenChange={(open) => !open && setSharing(null)} prompt={sharing} />
    </section>
  )
}
